import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { DEFAULT_BRANDING, readBrandingCache, type ResolvedBranding } from "@/lib/branding";

/** Org logo for the full-screen shell. Owns the synchronous branding-cache
 * read so in-page loaders never pay for it (see AppLoadingScreen's original
 * three-state rationale: matching cache → org logo; null/'mismatch' → the
 * neutral Blue Monk default, never the wrong org's mark). */
function BrandLogo() {
  const [branding] = useState<ResolvedBranding>(() => {
    const cached = readBrandingCache();
    return cached && cached !== "mismatch" ? cached : DEFAULT_BRANDING;
  });

  return (
    <img
      src={branding.loadingLogoUrl}
      alt={branding.displayName}
      className="h-10 opacity-90"
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = "none";
      }}
    />
  );
}

interface LoadingShellProps {
  /** Full-viewport centring + org logo. Only for pre-layout surfaces (auth
   * boot); in-page loaders must NOT use it (min-h-screen inside SidebarInset
   * overflows by the header height). */
  fullScreen?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * Shared chrome for loading surfaces: (optional) org logo + the sliding
 * loading bar, with a slot underneath for a message. Behaviour stays with the
 * caller — auth types its real phase, lazy-route fallbacks show the bar alone.
 */
export function LoadingShell({ fullScreen, className, children }: LoadingShellProps) {
  const { t } = useTranslation("common");
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center bg-background",
        fullScreen ? "min-h-screen" : "min-h-full p-8",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-8">
        {fullScreen && <BrandLogo />}

        <div className="w-48 h-1 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full animate-loading-bar" />
        </div>

        {/* When no typed line is supplied (bare lazy-route fallback), the
            shell itself must still announce loading to AT. */}
        {children ?? (
          <span role="status" aria-busy="true" className="sr-only">
            {t("loading")}
          </span>
        )}
      </div>
    </div>
  );
}
